import { lazy, Suspense, useEffect, useRef, useState, type ReactNode } from "react";
import { CreditCard, Lock, Plus } from "lucide-react";
import savedIllustration from "@/assets/froppio-welcome.svg";
import { Button } from "@/shared/components/shadcn/Button";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { LoadingSpinner } from "@/shared/components/ui/LoadingSpinner";
import { paymentMethodApiService as api } from "../services/paymentMethodApiService";
import {
  fromSetupConsentDtoToSetupConsent, fromSetupDetailsDtoToSetupDetails, fromSetupStatusDtoToSetupStatus,
  type SetupConsent, type SetupDetails, type SetupStatus,
} from "../types/paymentMethodTypes";
import { CardSetupLayout } from "./CardSetupLayout";

const StripeCardSetup = lazy(() => import("./StripeCardSetup"));

/** `staff`: the business owner types the card in, so the consent step is skipped and success finishes immediately. */
export function CardSetupFlow({ token, onDone, staff = false }: { token: string; onDone?: () => void; staff?: boolean }) {
  const [details, setDetails] = useState<SetupDetails>();
  const [setup, setSetup] = useState<SetupConsent>();
  const [status, setStatus] = useState<SetupStatus>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);
  const [checking, setChecking] = useState(false);
  const [confirmationAttempted, setConfirmationAttempted] = useState(false);
  const controller = useRef<AbortController | null>(null);
  const submitting = useRef(false);
  const returnUrl = `${window.location.origin}/payment-methods/setup#token=${encodeURIComponent(token)}`;

  useEffect(() => {
    const current = new AbortController();
    controller.current = current;
    async function load() {
      try {
        const info = fromSetupDetailsDtoToSetupDetails(await api.open(token, current.signal));
        const result = fromSetupStatusDtoToSetupStatus(await api.status(token, current.signal));
        const accepted = staff && result.status === "pending"
          ? fromSetupConsentDtoToSetupConsent(await api.consent(token, info.consentVersion, current.signal))
          : undefined;
        if (accepted && (accepted.stripeAccountId !== info.stripeAccountId || accepted.publishableKey !== info.publishableKey)) {
          throw new Error("Stripe setup context changed.");
        }
        if (!current.signal.aborted) { setDetails(info); setStatus(result); setSetup(accepted); }
      } catch {
        if (!current.signal.aborted) setError(staff
          ? "Couldn't start secure card setup. No charge was made. Please try again."
          : "This card setup link is unavailable or expired. Try again, or ask the business for a new link.");
      }
    }
    void load();
    return () => current.abort();
  }, [token, attempt, staff]);

  async function checkStatus() {
    const signal = controller.current?.signal;
    if (!signal || signal.aborted) return;
    setChecking(true);
    setError("");
    try {
      const result = fromSetupStatusDtoToSetupStatus(await api.status(token, signal));
      if (!signal.aborted) setStatus(result);
      return result;
    } catch {
      if (!signal.aborted) setError("Couldn't check whether the card was saved. Refresh status before submitting again.");
    } finally {
      if (!signal.aborted) setChecking(false);
    }
  }
  async function accept() {
    const signal = controller.current?.signal;
    if (!details || submitting.current || !signal || signal.aborted) return;
    submitting.current = true;
    setBusy(true);
    setError("");
    try {
      const result = fromSetupConsentDtoToSetupConsent(await api.consent(token, details.consentVersion, signal));
      if (result.stripeAccountId !== details.stripeAccountId || result.publishableKey !== details.publishableKey) {
        throw new Error("Stripe setup context changed.");
      }
      if (!signal.aborted) setSetup(result);
    } catch {
      if (!signal.aborted) setError("Couldn't start secure card setup. No charge was made. Please try again.");
    } finally {
      submitting.current = false;
      if (!signal.aborted) setBusy(false);
    }
  }
  const shell = (content: ReactNode) => staff ? content : <CardSetupLayout details={details}>{content}</CardSetupLayout>;
  if (!details || !status) return shell(error
    ? <ErrorState message={error} onRetry={() => { setError(""); setAttempt(value => value + 1); }} />
    : <LoadingSpinner message="Loading card setup..." />);
  if (status.status === "succeeded") return staff ? <div className="space-y-4">
    <p role="status" className="font-medium">Card saved. No payment was taken.</p>
    {status.card && <p>{status.card.brand} ending in {status.card.last4}</p>}
    <p className="text-sm text-muted-foreground">Your card is stored by Stripe for {details.businessName}.</p>
    {onDone ? <Button onClick={onDone}>Done</Button> : <p>You can close this page.</p>}
  </div> : <CardSetupLayout details={details} title="Card saved" showCard={false}>
    <div className="flex flex-col items-center gap-3 text-center">
      <img src={savedIllustration} width={280} height={240} alt="A happy Froppio" className="h-44 w-auto" />
      <p role="status" className="text-xl font-bold">Card saved. No payment was taken.</p>
      <p data-telemetry-mask className="text-sm text-muted-foreground">Your card is stored by Stripe for {details.businessName}.</p>
      {status.card && <p data-telemetry-mask className="flex w-full items-center gap-3 rounded-2xl border border-border bg-card px-4 py-3 text-left text-sm">
        <CreditCard aria-hidden className="size-5 shrink-0 text-primary" />
        <span className="flex-1"><b className="capitalize">{status.card.brand}</b> ending in {status.card.last4}</span>
      </p>}
      {onDone ? <Button className="h-12 w-full" onClick={onDone}>Done</Button> : <p className="text-sm text-muted-foreground">You can close this page.</p>}
    </div>
  </CardSetupLayout>;
  if (status.status === "canceled") return shell(<ErrorState message="This card setup was canceled. Ask the business for a new link." />);
  const pendingNotice = confirmationAttempted && !checking && !error && <p role="status" className="text-sm">
    {staff ? "Not completed yet." : "Card setup is still pending. Complete any bank verification, then refresh setup status."}
  </p>;
  const refreshStatus = <Button variant="quiet" className={staff ? undefined : "w-full"} disabled={busy || checking} onClick={() => void checkStatus()}>{checking ? "Checking card setup..." : "Refresh setup status"}</Button>;
  if (staff && setup) return <div className="space-y-4">
    <p className="whitespace-pre-wrap text-xs text-muted-foreground">{details.consentText}</p>
    <Suspense fallback={<LoadingSpinner message="Loading secure card form..." />}>
      <StripeCardSetup key={setup.clientSecret} setup={setup} returnUrl={returnUrl} onConfirmed={async () => {
        setConfirmationAttempted(true);
        if ((await checkStatus())?.status === "succeeded") onDone?.();
      }} />
    </Suspense>
    {pendingNotice}
    {error && <ErrorState message={error} />}
    {confirmationAttempted && <Button className="w-full" disabled={checking} onClick={async () => {
      if ((await checkStatus())?.status === "succeeded") onDone?.();
    }}>{checking ? "Checking..." : "Check if completed"}</Button>}
  </div>;
  return <CardSetupLayout details={details}>
    {!setup ? <>
      <details className="group rounded-2xl border border-border bg-card px-4 py-3 text-sm">
        <summary className="flex min-h-6 cursor-pointer list-none items-center justify-between gap-3 font-semibold [&::-webkit-details-marker]:hidden">
          What you're agreeing to
          <Plus aria-hidden className="size-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-45" />
        </summary>
        <p data-telemetry-mask className="mt-2.5 whitespace-pre-wrap leading-relaxed text-muted-foreground">{details.consentText}</p>
      </details>
      <p className="text-center text-xs leading-relaxed text-muted-foreground">
        By tapping Agree, you confirm you're the customer and agree to save your card for the future payments described above.
        Stripe may ask your bank to verify the card.
      </p>
      <Button className="h-12 w-full" disabled={busy} onClick={() => void accept()}>{busy ? "Preparing secure form..." : "Agree"}</Button>
    </> : <Suspense fallback={<LoadingSpinner message="Loading secure card form..." />}>
      <StripeCardSetup key={setup.clientSecret} setup={setup} returnUrl={returnUrl} onConfirmed={async () => {
        setConfirmationAttempted(true);
        await checkStatus();
      }} />
    </Suspense>}
    {pendingNotice}
    {error && <ErrorState message={error} />}
    {refreshStatus}
    <p className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
      <Lock aria-hidden className="size-3.5" />
      Secured by Stripe · Link expires {details.expiresAt.toLocaleString()}
    </p>
  </CardSetupLayout>;
}
